import { useState, useEffect } from "react";
import { Field, Label, Switch, Description, Input, Button } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/solid";
import { handleEnterKey } from "../../app/keyboardUtils";
import SimpleListbox from "../../components/input/SimpleListbox";
import { AdvancedSettingsViewModel } from "./promptsApiSlice";

interface OptionsStepProps {
	model: AdvancedSettingsViewModel;
	setAdvancedModel: (model: AdvancedSettingsViewModel) => void;
	setHasError: (hasError: boolean) => void;
}

export default function OptionsStep(props: OptionsStepProps) {
	const { model, setAdvancedModel, setHasError } = props;

	const [timerEnabled, setTimerEnabled] = useState(model.timerEnabled);
	const [timeAllowed, setTimeAllowed] = useState(model.timeAllowed);
	const [resubmitEnabled, setResubmitEnabled] = useState(model.retriesEnabled);
	const [resubmitAmount, setResubmitAmount] = useState(model.retriesAllowed);
	const [finalizeEnabled, setFinalizeEnabled] = useState(model.finalizeEnabled);
	const [finalizeNumber, setFinalizeNumber] = useState(model.finalizeDraftNumber);
	const [scoreHiddenNumber, setScoreHiddenNumber] = useState(model.scoreDraftNumber);
	const [grammarHiddenNumber, setGrammarHiddenNumber] = useState(
		model.grammarCheckDraftThreshold
	);
	const [plagiarismEnabled, setPlagiarismEnabled] = useState(model.plagiarismCheckerEnabled);
	const [plagiarismCheckerAllDrafts, setPlagiarismCheckerAllDrafts] = useState(
		model.plagiarismCheckerAllDrafts
	);
	const [plagiarismCheckedDrafts, setPlagiarismCheckedDrafts] = useState(
		model.plagiarismCheckedDrafts
	);
	const [titleRequired, setTitleRequired] = useState(model.titleRequired);
	const [sourcesRequired, setSourcesRequired] = useState(model.requireSources);
	const [copyPasteEnabled, setCopyPasteEnabled] = useState(model.allowCopyPaste);
	const [newWord, setNewWord] = useState("");
	const [vocab, setVocab] = useState(model.specializedVocabularyList ?? []);

	function handleAddNewWord() {
		setVocab([...vocab, newWord]);
		setNewWord("");
	}

	function handleRemoveWord(e: React.MouseEvent<HTMLElement>, target: string) {
		e.preventDefault();
		if (!vocab) return;
		var newVocabList = vocab.filter((word) => word != target);
		setVocab(newVocabList);
	}

	useEffect(() => {
		setAdvancedModel(updateModel());

		if (timerEnabled && (timeAllowed < 30 || timeAllowed > 90)) {
			setHasError(true);
		} else if (resubmitEnabled && (resubmitAmount < 1 || resubmitAmount > 99)) {
			setHasError(true);
		} else if (finalizeEnabled && (finalizeNumber < 1 || finalizeNumber > 99)) {
			setHasError(true);
		} else if (scoreHiddenNumber < 1 || scoreHiddenNumber > 99) {
			setHasError(true);
		} else if (grammarHiddenNumber < 1 || grammarHiddenNumber > 99) {
			setHasError(true);
		} else if (plagiarismEnabled && !plagiarismCheckerAllDrafts && !plagiarismCheckedDrafts) {
			setHasError(true);
		} else {
			setHasError(false);
		}
	}, [
		resubmitEnabled,
		resubmitAmount,
		timerEnabled,
		timeAllowed,
		finalizeEnabled,
		finalizeNumber,
		scoreHiddenNumber,
		grammarHiddenNumber,
		plagiarismEnabled,
		plagiarismCheckerAllDrafts,
		plagiarismCheckedDrafts,
		titleRequired,
		sourcesRequired,
		vocab,
		copyPasteEnabled,
	]);

	function updateModel(): AdvancedSettingsViewModel {
		const advancedModel: AdvancedSettingsViewModel = {
			...model,
			timerEnabled,
			timeAllowed,
			retriesEnabled: resubmitEnabled,
			retriesAllowed: resubmitAmount,
			finalizeEnabled,
			finalizeDraftNumber: finalizeNumber,
			scoreDraftNumber: scoreHiddenNumber,
			grammarCheckDraftThreshold: grammarHiddenNumber,
			plagiarismCheckerEnabled: plagiarismEnabled,
			plagiarismCheckerAllDrafts,
			plagiarismCheckedDrafts,
			titleRequired,
			requireSources: sourcesRequired,
			specializedVocabularyList: vocab,
			allowCopyPaste: copyPasteEnabled,
		};
		return advancedModel;
	}

	return (
		<section className="space-y-6">
			<div className="flex border-b border-gray-900/10 pb-4">
				<div className="w-full md:w-1/4 pr-3">
					<h2 className="leading-7">Copy & Paste</h2>
					<p className="mt-1 mr-6 text-sm leading-6 text-gray-600">
						Allow students to copy and paste text from other sources into their essay
					</p>
				</div>
				<div className="flex flex-col w-full md:w-3/4 gap-y-4">
					<Field className="flex items-center justify-between">
						<Label as="span" className="block font-medium leading-6">
							Enable Copy & Paste
						</Label>
						<Switch
							checked={copyPasteEnabled}
							onChange={setCopyPasteEnabled}
							className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
							<span
								aria-hidden="true"
								className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
							/>
						</Switch>
					</Field>
				</div>
			</div>
			<div className="flex border-b border-gray-900/10 pb-4">
				<div className="w-full md:w-1/4 pr-3">
					<h2 className="leading-7">Timer</h2>
					<p className="mt-1 mr-6 text-sm leading-6 text-gray-600">
						Enable a timer on student drafts
					</p>
				</div>
				<div className="flex flex-col w-full md:w-3/4 gap-y-4">
					<Field className="flex items-center justify-between">
						<Label as="span" className="block font-medium leading-6">
							Enable Timer
						</Label>
						<Switch
							checked={timerEnabled}
							onChange={setTimerEnabled}
							className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
							<span
								aria-hidden="true"
								className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
							/>
						</Switch>
					</Field>
					{timerEnabled && (
						<Field>
							<div className="flex">
								<Label as="span" className="block font-medium leading-6">
									Time Allowed
								</Label>
								<Description
									as="span"
									className="text-sm pl-1.5 leading-6 text-gray-600">
									(between 30 and 90 minutes)
								</Description>
							</div>
							<Input
								id="timeAllowed"
								name="timeAllowed"
								type="number"
								min={30}
								max={90}
								defaultValue={timeAllowed}
								className={`${timeAllowed < 30 || timeAllowed > 90 ? "focus:ring-red-600 ring-red-500" : "focus:ring-primary-600 ring-gray-300"} mt-2 resize-none w-full h-10 text-sm rounded-sm border-0 ring-1 ring-inset focus:ring-2 focus:ring-inset`}
								onChange={(e) => setTimeAllowed(Number(e.target.value))}></Input>
						</Field>
					)}
				</div>
			</div>
			<div className="flex border-b border-gray-900/10 pb-4">
				<div className="w-full md:w-1/4 pr-3">
					<h2 className="leading-7">Resubmit Drafts</h2>
					<p className="mt-1 mr-6 text-sm leading-6 text-gray-600">
						Control the number of drafts a student can submit
					</p>
				</div>
				<div className="flex flex-col w-full md:w-3/4 gap-y-4">
					<Field className="flex items-center justify-between">
						<Label as="span" className="block font-medium leading-6">
							Enable Revisions
						</Label>
						<Switch
							checked={resubmitEnabled}
							onChange={setResubmitEnabled}
							className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
							<span
								aria-hidden="true"
								className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
							/>
						</Switch>
					</Field>
					{resubmitEnabled && (
						<Field>
							<div className="flex">
								<Label as="span" className="block font-medium leading-6">
									Revisions Allowed
								</Label>
								<Description
									as="span"
									className="text-sm pl-1.5 leading-6 text-gray-600">
									(between 1 and 99 revisions)
								</Description>
							</div>
							<Input
								id="revisionAmount"
								name="revisionAmount"
								type="number"
								min={1}
								max={99}
								defaultValue={resubmitAmount}
								className={`${resubmitAmount < 1 || resubmitAmount > 99 ? "focus:ring-red-600 ring-red-500" : "focus:ring-primary-600 ring-gray-300"} mt-2 resize-none w-full h-10 text-sm rounded-sm border-0 ring-1 ring-inset focus:ring-2 focus:ring-inset`}
								onChange={(e) => setResubmitAmount(Number(e.target.value))}></Input>
						</Field>
					)}
				</div>
			</div>
			<div className="flex border-b border-gray-900/10 pb-4">
				<div className="w-full md:w-1/4 pr-3">
					<h2 className="leading-7">Finalize Essay</h2>
					<p className="mt-1 mr-6 text-sm leading-6 text-gray-600">
						Allow students to mark an essay as final
					</p>
				</div>
				<div className="flex flex-col w-full md:w-3/4 gap-y-4">
					<Field className="flex items-center justify-between">
						<Label as="span" className="block font-medium leading-6">
							Enable Finalize
						</Label>
						<Switch
							checked={finalizeEnabled}
							onChange={setFinalizeEnabled}
							className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
							<span
								aria-hidden="true"
								className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
							/>
						</Switch>
					</Field>
					{finalizeEnabled && (
						<Field>
							<div className="flex">
								<Label as="span" className="block font-medium leading-6">
									Hide Finalize Essay Button until Draft Number
								</Label>
								<Description
									as="span"
									className="text-sm pl-1.5 leading-6 text-gray-600">
									(between 1 and 99 drafts)
								</Description>
							</div>
							<Input
								id="finalizeAmount"
								name="finalizeAmount"
								type="number"
								min={1}
								max={99}
								defaultValue={finalizeNumber}
								className={`${finalizeNumber < 1 || finalizeNumber > 99 ? "focus:ring-red-600 ring-red-500" : "focus:ring-primary-600 ring-gray-300"} mt-2 resize-none w-full h-10 text-sm rounded-sm border-0 ring-1 ring-inset focus:ring-2 focus:ring-inset`}
								onChange={(e) => setFinalizeNumber(Number(e.target.value))}></Input>
						</Field>
					)}
				</div>
			</div>
			<div className="flex border-b border-gray-900/10 pb-4">
				<div className="w-full md:w-1/4 pr-3">
					<h2 className="leading-7">Hide Score</h2>
				</div>
				<div className="flex flex-col w-full md:w-3/4 gap-y-4">
					<Field>
						<div className="flex">
							<Label as="span" className="block font-medium leading-6">
								Hide Score until Draft Number
							</Label>
							<Description
								as="span"
								className="text-sm pl-1.5 leading-6 text-gray-600">
								(between 1 and 99 drafts)
							</Description>
						</div>
						<Input
							id="scoreHiddenNumber"
							name="scoreHiddenNumber"
							type="number"
							min={1}
							max={99}
							defaultValue={scoreHiddenNumber}
							className={`${scoreHiddenNumber < 1 || scoreHiddenNumber > 99 ? "focus:ring-red-600 ring-red-500" : "focus:ring-primary-600 ring-gray-300"} mt-2 resize-none w-full h-10 text-sm rounded-sm border-0 ring-1 ring-inset focus:ring-2 focus:ring-inset`}
							onChange={(e) => setScoreHiddenNumber(Number(e.target.value))}></Input>
					</Field>
				</div>
			</div>
			<div className="flex border-b border-gray-900/10 pb-4">
				<div className="w-full md:w-1/4 pr-3">
					<h2 className="leading-7">Grammar & Spelling</h2>
				</div>
				<div className="flex flex-col w-full md:w-3/4 gap-y-4">
					<Field>
						<div className="flex">
							<Label as="span" className="block font-medium leading-6">
								Hide Grammar and Spelling Feedback until Draft Number
							</Label>
							<Description
								as="span"
								className="text-sm pl-1.5 leading-6 text-gray-600">
								(between 1 and 99 drafts)
							</Description>
						</div>
						<Input
							id="grammarHiddenNumber"
							name="grammarHiddenNumber"
							type="number"
							min={1}
							max={99}
							defaultValue={grammarHiddenNumber}
							className={`${grammarHiddenNumber < 1 || grammarHiddenNumber > 99 ? "focus:ring-red-600 ring-red-500" : "focus:ring-primary-600 ring-gray-300"} mt-2 resize-none w-full h-10 text-sm rounded-sm border-0 ring-1 ring-inset focus:ring-2 focus:ring-inset`}
							onChange={(e) =>
								setGrammarHiddenNumber(Number(e.target.value))
							}></Input>
					</Field>
				</div>
			</div>
			{model.canSetPlagiarismSettings && (
				<div id="PlagiarismSettings" className="flex border-b border-gray-900/10 pb-4">
					<div className="w-full md:w-1/4 pr-3">
						<h2 className="leading-7">Plagiarism Checker</h2>
						<p className="mt-1 mr-6 text-sm leading-6 text-gray-600">
							Have student drafts checked for plagiarism.
						</p>
					</div>
					<div className="flex flex-col w-full md:w-3/4 gap-y-4">
						<Field className="flex items-center justify-between">
							<Label as="span" className="block font-medium leading-6">
								Enable Plagiarism Checker
							</Label>
							<Switch
								checked={plagiarismEnabled}
								onChange={setPlagiarismEnabled}
								className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
								<span
									aria-hidden="true"
									className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
								/>
							</Switch>
						</Field>
						{plagiarismEnabled && (
							<Field>
								<Label as="span" className="block font-medium leading-6">
									Which drafts do you want checked?
								</Label>
								<SimpleListbox
									id="plagiarismCheckedDrafts"
									options={[
										{ value: "all", label: "All Drafts" },
										{
											value: "specific",
											label: "Specific Drafts",
										},
									]}
									value={plagiarismCheckerAllDrafts ? "all" : "specific"}
									className={
										"w-full focus:ring-1 focus:ring-inset focus:!ring-primary-600 !py-1.5 mt-2"
									}
									onChange={(e) =>
										setPlagiarismCheckerAllDrafts(e.target.value === "all")
									}
								/>
								{!plagiarismCheckerAllDrafts && (
									<Input
										id="plagiarismDrafts"
										name="plagiarismDrafts"
										type="text"
										placeholder="Enter the draft numbers you would like checked, separated by commas..."
										value={plagiarismCheckedDrafts}
										className={`${!plagiarismCheckedDrafts ? "focus:ring-red-600 ring-red-500" : "focus:ring-primary-600 ring-gray-300"} mt-2 resize-none w-full h-10 text-sm rounded-sm border-0 ring-1 ring-inset focus:ring-2 focus:ring-inset`}
										onChange={(e) =>
											setPlagiarismCheckedDrafts(e.target.value)
										}></Input>
								)}
							</Field>
						)}
					</div>
				</div>
			)}
			<div className="flex border-b border-gray-900/10 pb-4">
				<div className="w-full md:w-1/4 pr-3">
					<h2 className="leading-7">Essay Title</h2>
				</div>
				<div className="flex flex-col w-full md:w-3/4 gap-y-4">
					<Field className="flex items-center justify-between">
						<Label as="span" className="block font-medium leading-6">
							Require Essay Title
						</Label>
						<Switch
							checked={titleRequired}
							onChange={setTitleRequired}
							className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
							<span
								aria-hidden="true"
								className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
							/>
						</Switch>
					</Field>
				</div>
			</div>
			<div className="flex border-b border-gray-900/10 pb-4">
				<div className="w-full md:w-1/4 pr-3">
					<h2 className="leading-7">Cited Sources</h2>
				</div>
				<div className="flex flex-col w-full md:w-3/4 gap-y-4">
					<Field className="flex items-center justify-between">
						<Label as="span" className="block font-medium leading-6">
							Require Cited Sources
						</Label>
						<Switch
							checked={sourcesRequired}
							onChange={setSourcesRequired}
							className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
							<span
								aria-hidden="true"
								className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
							/>
						</Switch>
					</Field>
				</div>
			</div>
			<div className="flex pb-4">
				<div className="w-full md:w-1/4 pr-3">
					<h2 className="leading-7">Specialized Vocabulary</h2>
					<p className="mt-1 text-sm leading-6 mr-6 text-gray-600">
						Add uncommon technical or content-related words you don’t want marked as
						misspelled
					</p>
				</div>
				<div className="flex flex-col w-full md:w-3/4 gap-y-4">
					<Field>
						<Label as="span" className="block font-medium leading-6">
							Specialized Vocabulary
						</Label>
						{vocab.length > 0 &&
							vocab.map((word, index) => (
								<span
									key={"word-" + index}
									className="border border-gray-300 rounded-md p-2 mr-2 my-1 inline-block">
									{word}
									<button
										className="pl-2"
										onClick={(e) => handleRemoveWord(e, word)}>
										⨯
									</button>
								</span>
							))}
						<div className="flex mt-2 h-12 rounded-md shadow-sm">
							<Input
								type="text"
								name="newWord"
								id="newWord"
								className="w-full rounded-none rounded-l-md border-0 py-1.5 px-3 text-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600"
								placeholder="Add a specialized vocabulary word..."
								value={newWord}
								onChange={(e) => setNewWord(e.target.value)}
								onKeyDown={(e) => handleEnterKey(e, handleAddNewWord)}></Input>
							<Button
								type="button"
								disabled={!newWord}
								onClick={handleAddNewWord}
								className="relative inline-flex w-48 items-center rounded-r-md px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed">
								<span className="flex items-center">
									<PlusIcon className="size-4 mr-2" /> Add Vocabulary
								</span>
							</Button>
						</div>
					</Field>
				</div>
			</div>
		</section>
	);
}
